import * as LocalAuthentication from 'expo-local-authentication';
import * as SecureStore from 'expo-secure-store';
import { Platform } from 'react-native';
import logger from './logger';

export interface BiometricConfig {
  enabled: boolean;
  type: 'fingerprint' | 'facial' | 'iris' | 'none';
}

/**
 * Check if biometric authentication is available on the device
 */
export const checkBiometricAvailability = async (): Promise<BiometricConfig> => {
  try {
    const hasHardware = await LocalAuthentication.hasHardwareAsync();
    const isEnrolled = await LocalAuthentication.isEnrolledAsync();

    if (!hasHardware) {
      return { enabled: false, type: 'none' };
    }

    if (!isEnrolled) {
      // Device has hardware but no biometrics enrolled
      return { enabled: false, type: 'none' };
    }

    const supportedTypes = await LocalAuthentication.supportedAuthenticationTypesAsync();

    // Determine biometric type
    let type: BiometricConfig['type'] = 'none';
    if (supportedTypes.includes(LocalAuthentication.AuthenticationType.FACIAL_RECOGNITION)) {
      type = 'facial';
    } else if (supportedTypes.includes(LocalAuthentication.AuthenticationType.FINGERPRINT)) {
      type = 'fingerprint';
    } else if (supportedTypes.includes(LocalAuthentication.AuthenticationType.IRIS)) {
      type = 'iris';
    }

    return { enabled: true, type };
  } catch (error) {
    // Error checking biometric availability - permissions might be denied
    logger.warn('Biometric availability check failed:', error);
    return { enabled: false, type: 'none' };
  }
};

/**
 * Authenticate user with biometrics
 */
export const authenticateWithBiometrics = async (): Promise<boolean> => {
  try {
    const result = await LocalAuthentication.authenticateAsync({
      promptMessage: 'Authenticate to access IoT Edge',
      cancelLabel: 'Cancel',
      disableDeviceFallback: false,
      fallbackLabel: 'Use passcode',
    });

    return result.success;
  } catch (error) {
    return false;
  }
};

/**
 * Enable biometric authentication (stores flag only, not credentials)
 * Security: Uses existing auth tokens instead of storing passwords
 */
export const saveBiometricCredentials = async (email: string): Promise<void> => {
  if (Platform.OS === 'web') return; // Not supported on web

  await SecureStore.setItemAsync('biometric_email', email);
  await SecureStore.setItemAsync('biometric_enabled', 'true');
  // Security fix: No longer storing password, rely on existing tokens
};

/**
 * Get biometric email (no password retrieval for security)
 */
export const getBiometricCredentials = async (): Promise<{ email: string } | null> => {
  if (Platform.OS === 'web') return null;

  try {
    const email = await SecureStore.getItemAsync('biometric_email');
    const enabled = await SecureStore.getItemAsync('biometric_enabled');

    if (email && enabled === 'true') {
      return { email };
    }

    return null;
  } catch (error) {
    return null;
  }
};

/**
 * Disable biometric authentication
 */
export const disableBiometric = async (): Promise<void> => {
  if (Platform.OS === 'web') return;

  await SecureStore.deleteItemAsync('biometric_email');
  await SecureStore.deleteItemAsync('biometric_enabled');
  // Security fix: biometric_password no longer exists
};

/**
 * Check if biometric is enabled
 */
export const checkBiometricEnabled = async (): Promise<boolean> => {
  if (Platform.OS === 'web') return false;

  try {
    const enabled = await SecureStore.getItemAsync('biometric_enabled');
    return enabled === 'true';
  } catch (error) {
    logger.error('Failed to check biometric status:', error);
    return false;
  }
};
